import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiDollarSign, FiShield, FiCheckCircle, FiArrowRight, FiUsers, FiHome, FiTrendingUp, FiCalendar, FiBriefcase, FiHeart, FiX, FiTarget, FiBook, FiTruck, FiStar } = FiIcons;

const TermLifePage = () => {
  const benefits = [
    'Coverage amounts from $100,000 to $3 million',
    'Fixed premiums that never increase',
    'No medical exam for most applicants',
    'Choose 10, 20, or 30-year terms',
    'Convertible to permanent life insurance',
    'Accelerated death benefit rider available'
  ];

  const whoShouldConsider = [
    {
      icon: FiUsers,
      title: 'Young Families (25-45)',
      description: 'Parents with children and financial dependents',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      scenarios: [
        'Mortgage protection needed',
        'Children\'s education costs',
        'Single or dual income families',
        'Growing family expenses'
      ]
    },
    {
      icon: FiHome,
      title: 'Homeowners',
      description: 'Property owners with mortgages or significant debt',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        '15-30 year mortgages',
        'Home equity loans',
        'Property taxes & maintenance',
        'Ensure family keeps home'
      ]
    },
    {
      icon: FiBriefcase,
      title: 'Primary Income Earners',
      description: 'Main breadwinners supporting family lifestyle',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      scenarios: [
        'Replace lost income',
        'Maintain living standards',
        'Support spouse & children',
        'Cover ongoing expenses'
      ]
    },
    {
      icon: FiTarget,
      title: 'Temporary Needs',
      description: 'Specific financial obligations with end dates',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      scenarios: [
        'Business loans',
        'Children until independent',
        'Spouse until retirement',
        'Specific debt payoff'
      ]
    }
  ];

  const whoShouldConsiderWhole = [
    {
      icon: FiStar,
      title: 'Seniors (66+)',
      description: 'Older adults needing guaranteed acceptance',
      reason: 'Age Restrictions',
      alternative: 'Whole life offers guaranteed approval for seniors'
    },
    {
      icon: FiBook,
      title: 'Estate Planners',
      description: 'High net worth individuals planning wealth transfer',
      reason: 'Permanent Need',
      alternative: 'Whole life provides permanent coverage & tax benefits'
    },
    {
      icon: FiTrendingUp,
      title: 'Conservative Investors',
      description: 'Want guaranteed returns with life insurance',
      reason: 'Investment Component',
      alternative: 'Whole life builds cash value with guaranteed growth'
    },
    {
      icon: FiShield,
      title: 'Permanent Protection',
      description: 'Need lifetime coverage regardless of health changes',
      reason: 'Lifetime Coverage',
      alternative: 'Whole life never expires, unlike term policies'
    }
  ];

  const whoShouldNotConsider = [
    {
      icon: FiDollarSign,
      title: 'Very Limited Budget',
      description: 'Cannot afford even basic term life premiums',
      reason: 'Budget Constraints',
      alternative: 'Consider group life insurance through employer first'
    },
    {
      icon: FiCalendar,
      title: 'No Dependents',
      description: 'Single with no financial dependents or obligations',
      reason: 'No Financial Need',
      alternative: 'Focus on building emergency fund and investments'
    },
    {
      icon: FiTruck,
      title: 'Financially Independent',
      description: 'Enough assets to self-insure family needs',
      reason: 'Self-Insurance Possible',
      alternative: 'May only need coverage for estate taxes or liquidity'
    },
    {
      icon: FiX,
      title: 'Serious Health Issues',
      description: 'Major health conditions making coverage expensive',
      reason: 'Health Limitations',
      alternative: 'Consider guaranteed issue or group coverage options'
    }
  ];

  const termComparison = [
    {
      term: '10-Year Term',
      icon: FiClock,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      idealFor: 'Short-term debt coverage',
      scenarios: [
        'Business loans',
        'Personal debts',
        'Bridge coverage'
      ],
      pros: [
        'Lowest premiums',
        'Short commitment'
      ],
      cons: [
        'Rates increase at renewal',
        'Limited coverage period'
      ],
      bestAge: '25-35'
    },
    {
      term: '20-Year Term',
      icon: FiBriefcase,
      color: 'bg-green-500',
      lightColor: 'bg-green-50',
      textColor: 'text-green-600',
      idealFor: 'Family protection',
      scenarios: [
        'Mortgage protection',
        'Children\'s education',
        'Income replacement'
      ],
      pros: [
        'Balanced cost & coverage',
        'Most popular choice'
      ],
      cons: [
        'Higher than 10-year',
        'May outlive needs'
      ],
      bestAge: '30-45'
    },
    {
      term: '30-Year Term',
      icon: FiHeart,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      idealFor: 'Long-term security',
      scenarios: [
        'Young children',
        '30-year mortgages',
        'Retirement planning'
      ],
      pros: [
        'Longest protection',
        'Rates locked for 30 years'
      ],
      cons: [
        'Highest premiums',
        'Long commitment'
      ],
      bestAge: '25-40'
    }
  ];

  const idealFor = [
    {
      icon: FiUsers,
      title: 'Young Families',
      description: 'Protect your growing family with affordable coverage during your highest-need years.'
    },
    {
      icon: FiHome,
      title: 'Homeowners',
      description: 'Ensure your mortgage is paid off if something happens to you.'
    },
    {
      icon: FiTrendingUp,
      title: 'Income Earners',
      description: 'Replace lost income to maintain your family\'s lifestyle and future plans.'
    }
  ];

  const testimonials = [
    {
      name: 'Amanda K.',
      age: 32,
      location: 'San Francisco, CA',
      quote: 'With two young kids and a mortgage, term life insurance was the perfect solution. The 30-year policy will cover us until the kids are grown and the house is paid off.',
      coverage: '$750,000 • 30-year term'
    },
    {
      name: 'James R.',
      age: 28,
      location: 'Los Angeles, CA',
      quote: 'Just got married and wanted to make sure my wife would be financially secure. The application process was incredibly easy and fast.',
      coverage: '$500,000 • 20-year term'
    },
    {
      name: 'Lisa M.',
      age: 45,
      location: 'San Diego, CA',
      quote: 'As a single mom, I needed affordable protection for my teenage daughter. Term life gave me the coverage I needed at a price I could afford.',
      coverage: '$400,000 • 15-year term'
    }
  ];

  const pricingData = [
    { age: '25-30', male: '$18', female: '$15' },
    { age: '31-35', male: '$22', female: '$19' },
    { age: '36-40', male: '$28', female: '$24' },
    { age: '41-45', male: '$45', female: '$38' },
    { age: '46-50', male: '$72', female: '$58' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiClock} className="h-8 w-8 mr-3" />
                <span className="font-semibold">Term Life Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Affordable Protection When You Need It Most
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Get substantial life insurance coverage at the lowest cost. Perfect for young families, homeowners, and anyone with temporary financial obligations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My Price
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">Sample Term Life Rates</h3>
              <p className="text-sm text-secondary-600 mb-4">Monthly premiums for $500,000 coverage (20-year term)</p>
              <div className="space-y-3">
                {pricingData.map((row, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-secondary-200">
                    <span className="font-medium">{row.age}</span>
                    <div className="flex space-x-6">
                      <span className="text-blue-600">♂ ${row.male}/mo</span>
                      <span className="text-pink-600">♀ ${row.female}/mo</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-secondary-500 mt-4">
                *Rates for healthy, non-smoking applicants. Your actual rate may vary.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Consider Term Life - Infographic */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Is Term Life Insurance Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Term life insurance is ideal for specific life situations. Here's who benefits most, who should consider whole life instead, and who may not need coverage.
            </p>
          </motion.div>

          {/* Who SHOULD Consider Term Life */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-blue-700 mb-2">✅ Perfect Candidates for Term Life Insurance</h3>
              <p className="text-secondary-600">These situations make term life insurance the best choice</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${candidate.bgColor} ${candidate.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="text-center mb-4">
                    <div className={`${candidate.bgColor} rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border ${candidate.borderColor}`}>
                      <SafeIcon icon={candidate.icon} className={`h-8 w-8 ${candidate.color}`} />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-4">{candidate.description}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-secondary-800 mb-2 text-sm">Perfect for:</h5>
                    <ul className="space-y-1">
                      {candidate.scenarios.map((scenario, idx) => (
                        <li key={idx} className="text-xs text-secondary-700 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className={`h-3 w-3 ${candidate.color} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should Consider Whole Life Instead */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-2">🔄 Consider Whole Life Insurance Instead</h3>
              <p className="text-secondary-600">These situations may benefit more from whole life insurance</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsiderWhole.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-green-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-green-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-green-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-800">Why Whole Life:</p>
                      <p className="text-xs text-green-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-blue-800">Whole Life Benefit:</p>
                      <p className="text-xs text-blue-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should NOT Consider Life Insurance */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-red-700 mb-2">❌ May Not Need Life Insurance</h3>
              <p className="text-secondary-600">These situations may have better alternatives or no immediate need</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldNotConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-red-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-red-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-red-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-red-800">Why Skip:</p>
                      <p className="text-xs text-red-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-yellow-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-yellow-800">Consider Instead:</p>
                      <p className="text-xs text-yellow-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Decision Helper */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-white rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-secondary-900 text-center mb-8">
              Quick Decision Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-blue-800 mb-2">Choose Term Life If:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 text-left">
                    <li>• You have dependents</li>
                    <li>• You have debt (mortgage/loans)</li>
                    <li>• You need maximum coverage</li>
                    <li>• You want lowest premiums</li>
                    <li>• You're under 65 years old</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiShield} className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-green-800 mb-2">Choose Whole Life If:</h4>
                  <ul className="text-sm text-green-700 space-y-1 text-left">
                    <li>• You're 66+ years old</li>
                    <li>• You need permanent coverage</li>
                    <li>• You want cash value growth</li>
                    <li>• Estate planning is priority</li>
                    <li>• You want guaranteed approval</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-gray-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiX} className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-800 mb-2">Skip Life Insurance If:</h4>
                  <ul className="text-sm text-gray-700 space-y-1 text-left">
                    <li>• You have no dependents</li>
                    <li>• You're financially independent</li>
                    <li>• You have no debt</li>
                    <li>• You can't afford premiums</li>
                    <li>• You have serious health issues</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Term Comparison Infographic Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Which Term Length is Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Choose the term that matches your financial timeline and protection needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {termComparison.map((term, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                {/* Header */}
                <div className={`${term.color} text-white p-6 text-center`}>
                  <div className="flex items-center justify-center mb-3">
                    <SafeIcon icon={term.icon} className="h-8 w-8 mr-2" />
                    <h3 className="text-2xl font-bold">{term.term}</h3>
                  </div>
                  <p className="text-lg opacity-90">{term.idealFor}</p>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Best Age */}
                  <div className={`${term.lightColor} rounded-lg p-4 text-center`}>
                    <p className="text-sm font-medium text-secondary-600">Best for Ages</p>
                    <p className={`text-xl font-bold ${term.textColor}`}>{term.bestAge}</p>
                  </div>

                  {/* Scenarios */}
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-3">Perfect for:</h4>
                    <ul className="space-y-2">
                      {term.scenarios.map((scenario, idx) => (
                        <li key={idx} className="flex items-center text-sm text-secondary-700">
                          <SafeIcon icon={FiCheckCircle} className={`h-4 w-4 ${term.textColor} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Pros & Cons */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-green-600 mb-2 text-sm">Pros</h5>
                      <ul className="space-y-1">
                        {term.pros.map((pro, idx) => (
                          <li key={idx} className="text-xs text-secondary-600 flex items-start">
                            <span className="text-green-500 mr-1">+</span>
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-red-600 mb-2 text-sm">Cons</h5>
                      <ul className="space-y-1">
                        {term.cons.map((con, idx) => (
                          <li key={idx} className="text-xs text-secondary-600 flex items-start">
                            <span className="text-red-500 mr-1">-</span>
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quick Decision Guide */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-secondary-50 rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-secondary-900 text-center mb-8">
              Term Length Decision Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4 border-2 border-blue-200 rounded-lg bg-white">
                <SafeIcon icon={FiClock} className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="font-semibold text-secondary-900 mb-1">Choose 10-Year if:</p>
                <p className="text-sm text-secondary-600">You have short-term debt or need temporary coverage</p>
              </div>
              <div className="p-4 border-2 border-green-200 rounded-lg bg-white">
                <SafeIcon icon={FiBriefcase} className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-secondary-900 mb-1">Choose 20-Year if:</p>
                <p className="text-sm text-secondary-600">You have a mortgage and children under 18</p>
              </div>
              <div className="p-4 border-2 border-purple-200 rounded-lg bg-white">
                <SafeIcon icon={FiHeart} className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="font-semibold text-secondary-900 mb-1">Choose 30-Year if:</p>
                <p className="text-sm text-secondary-600">You have young children or a 30-year mortgage</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Why Choose Term Life Insurance?
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                Term life insurance provides maximum coverage at minimum cost, making it the most popular choice for young families and professionals.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-secondary-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Term Life vs. Whole Life</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-2">Term Life Insurance</h4>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• Lower premiums</li>
                    <li>• Higher coverage amounts</li>
                    <li>• Temporary protection</li>
                    <li>• No cash value</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-2">Whole Life Insurance</h4>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• Higher premiums</li>
                    <li>• Lifetime protection</li>
                    <li>• Builds cash value</li>
                    <li>• Investment component</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            Who Should Consider Term Life Insurance?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {idealFor.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 text-center border border-secondary-200 hover:shadow-lg transition-shadow"
              >
                <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <SafeIcon icon={item.icon} className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            What Our Term Life Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-lg"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-blue-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Protect Your Family with Term Life Insurance?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a free quote in minutes. No medical exam required for most applicants.
          </p>
          <a
            href="https://quote.burbankinsuranceservices.com/Dt2tCn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
          <p className="mt-4 text-sm text-blue-200">
            Application takes less than 5 minutes • Instant approval available
          </p>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default TermLifePage;